import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Circle, ArrowLeft, Sparkles, Sliders } from "lucide-react";
import { StatusBadge } from "./Requisitions";

const DEFAULT_WEIGHTS = { skills: 40, experience: 20, notice_period: 15, budget: 15, location: 10 };

export default function RequisitionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [cands, setCands] = useState([]);
  const [comment, setComment] = useState("");
  const [parsing, setParsing] = useState(false);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [showWeights, setShowWeights] = useState(false);

  const load = useCallback(() => {
    api.get(`/requisitions/${id}`).then((r) => {
      setReq(r.data);
      setWeights(r.data.scoring_weights || DEFAULT_WEIGHTS);
    });
    api.get(`/candidates?requisition_id=${id}`).then((r) => setCands(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const parseJD = async () => {
    setParsing(true);
    try {
      const { data } = await api.post(`/requisitions/${id}/ai/parse-jd`);
      toast.success(`JD parsed — ${data.must_have_skills?.length || 0} must-haves + ${data.ivr_questions?.length || 0} IVR questions`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setParsing(false); }
  };

  const saveWeights = async () => {
    try {
      await api.put(`/requisitions/${id}/scoring-weights`, weights);
      toast.success("Scoring weights updated");
      setShowWeights(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const totalWeight = weights.skills + weights.experience + weights.notice_period + weights.budget + weights.location;

  const submit = async () => {
    try {
      await api.post(`/requisitions/${id}/submit`);
      toast.success("Submitted for approval");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const act = async (action) => {
    try {
      await api.post(`/requisitions/${id}/approve`, { action, comment });
      toast.success(`Step ${action}d`);
      setComment("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!req) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const currentStep = req.approval_chain?.[req.current_step - 1];
  const isMyTurn = req.status === "pending_approval" && currentStep &&
    (user.role === currentStep.approver_role || user.role === "super_admin");

  return (
    <div data-testid="requisition-detail-page">
      <Link to="/requisitions" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground hover:text-[#0A251D]"><ArrowLeft size={13} /> Requisitions</Link>
      <div className="flex items-start justify-between flex-wrap gap-4 mt-2">
        <div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{req.title}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
            <StatusBadge status={req.status} />
            <span>{req.openings} opening(s)</span>
            <span>{req.experience_min}–{req.experience_max} yrs</span>
            <span className="capitalize">Priority: {req.priority}</span>
          </div>
        </div>
        {req.status === "draft" && (
          <button data-testid="submit-approval-button" onClick={submit}
            className="bg-[#0A251D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
            Submit for Approval
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="border bg-white rounded-xl p-6" data-testid="structured-jd-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Structured JD (AI-parsed)</div>
              <div className="flex gap-2">
                <button data-testid="parse-jd-button" onClick={parseJD} disabled={parsing || !req.job_description}
                  className="flex items-center gap-1.5 text-xs bg-[#E05A47] text-white px-3 py-1.5 font-bold hover:bg-[#C74936] transition-colors duration-150 disabled:opacity-60">
                  <Sparkles size={12} /> {parsing ? "Parsing…" : req.structured_jd ? "Re-parse" : "AI Parse JD"}
                </button>
                <button data-testid="weights-toggle" onClick={() => setShowWeights(!showWeights)}
                  className="flex items-center gap-1.5 text-xs border px-3 py-1.5 font-bold hover:border-[#0A251D] transition-colors duration-150">
                  <Sliders size={12} /> Weights
                </button>
              </div>
            </div>
            {showWeights && (
              <div className="border-2 border-[#0A251D] rounded-lg p-4 mb-4" data-testid="weights-editor">
                <div className="text-xs text-muted-foreground mb-2">Configure how AI scores match this role. Total must equal 100.</div>
                <div className="grid grid-cols-5 gap-2">
                  {Object.entries(weights).map(([k, v]) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{k.replace("_", " ")}</label>
                      <input data-testid={`weight-${k}`} type="number" value={v} onChange={(e) => setWeights({ ...weights, [k]: +e.target.value })} className="mt-1 w-full border px-2 py-1.5 text-sm" />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className={`text-xs font-bold ${totalWeight === 100 ? "text-emerald-700" : "text-red-600"}`}>Total: {totalWeight}/100</div>
                  <button data-testid="weights-save" onClick={saveWeights} disabled={totalWeight !== 100}
                    className="bg-[#0A251D] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-40">Save weights</button>
                </div>
              </div>
            )}
            {req.structured_jd ? (
              <div className="space-y-3 text-sm">
                <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Must-have skills</span><div className="flex flex-wrap gap-1.5 mt-1">{req.structured_jd.must_have_skills?.map((s, i) => <span key={i} className="text-xs bg-[#0A251D] text-white px-2 py-1 rounded font-semibold">{s}</span>)}</div></div>
                <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Nice-to-have</span><div className="flex flex-wrap gap-1.5 mt-1">{req.structured_jd.nice_to_have_skills?.map((s, i) => <span key={i} className="text-xs border border-[#0A251D] text-[#0A251D] px-2 py-1 rounded font-semibold">{s}</span>)}</div></div>
                <div className="grid grid-cols-2 gap-2 text-xs"><div><span className="text-muted-foreground">Industry:</span> <span className="font-semibold">{req.structured_jd.industry_experience}</span></div><div><span className="text-muted-foreground">Qualification:</span> <span className="font-semibold">{req.structured_jd.qualification}</span></div><div><span className="text-muted-foreground">Seniority:</span> <span className="font-semibold capitalize">{req.structured_jd.seniority}</span></div></div>
                <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Search keywords</span><div className="text-xs mt-1 text-muted-foreground">{req.structured_jd.keywords?.join(" · ")}</div></div>
                {req.structured_jd.ivr_questions && <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">AI IVR Questions</span><ol className="text-xs mt-1 space-y-0.5">{req.structured_jd.ivr_questions.map((q, i) => <li key={i}><span className="text-[#0A251D] font-semibold">{i + 1}.</span> {q}</li>)}</ol></div>}
              </div>
            ) : <div className="text-sm text-muted-foreground">Not parsed yet — click &ldquo;AI Parse JD&rdquo; to extract must-haves, keywords, and role-specific IVR questions.</div>}
          </div>

          <div className="border bg-white rounded-xl p-6">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Job Description</div>
            <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="req-jd-content">{req.job_description || "No JD yet."}</div>
          </div>
          <div className="border bg-white rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Candidates ({cands.length})</div>
              <Link to={`/candidates?requisition=${id}`} data-testid="open-in-pipeline-link" className="text-xs font-bold text-[#E05A47] hover:underline">Open in pipeline →</Link>
            </div>
            {cands.length === 0 && <div className="text-sm text-muted-foreground">No candidates yet.</div>}
            {cands.map((c) => (
              <Link key={c.id} to={`/candidates/${c.id}`} className="flex items-center justify-between border-b last:border-0 py-2.5 text-sm hover:text-[#0A251D]">
                <span className="font-medium">{c.name}</span>
                <div className="flex items-center gap-3">
                  {c.ai_screening && <span className="text-xs font-bold text-[#0A251D]">{c.ai_screening.overall_score}/100</span>}
                  <span className="text-[10px] uppercase tracking-wider bg-secondary px-2 py-1 rounded-md">{c.stage}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="border bg-white rounded-xl p-6 h-fit" data-testid="approval-timeline">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-4">Approval Workflow</div>
          {(!req.approval_chain || req.approval_chain.length === 0) && (
            <div className="text-sm text-muted-foreground">Not yet submitted.</div>
          )}
          {req.approval_chain?.map((s, i) => (
            <div key={i} className="flex gap-3 pb-5 last:pb-0 relative">
              {i < req.approval_chain.length - 1 && <div className="absolute left-[9px] top-6 bottom-0 w-px bg-border" />}
              {s.status === "approved" ? <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
                : s.status === "rejected" ? <XCircle size={20} className="text-red-600 shrink-0" />
                : <Circle size={20} className={`shrink-0 ${req.current_step === s.order && req.status === "pending_approval" ? "text-[#0A251D]" : "text-muted-foreground/40"}`} />}
              <div className="min-w-0">
                <div className="text-sm font-semibold">{s.label}</div>
                <div className="text-xs text-muted-foreground capitalize">{s.approver_role.replace(/_/g, " ")}</div>
                {s.actor_name && <div className="text-xs mt-1">{s.status} by {s.actor_name}</div>}
                {s.comment && <div className="text-xs italic text-muted-foreground mt-0.5">“{s.comment}”</div>}
              </div>
            </div>
          ))}
          {isMyTurn && (
            <div className="mt-4 border-t pt-4">
              <div className="text-xs font-semibold text-[#0A251D] uppercase tracking-widest mb-2">Your approval is required</div>
              <input data-testid="approval-comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Comment (optional)" className="w-full border px-3 py-2 text-sm mb-2" />
              <div className="flex gap-2">
                <button data-testid="approve-button" onClick={() => act("approve")}
                  className="flex-1 bg-emerald-600 text-white py-2 text-xs font-semibold uppercase tracking-widest hover:bg-emerald-700 transition-colors duration-150">Approve</button>
                <button data-testid="reject-button" onClick={() => act("reject")}
                  className="flex-1 bg-red-600 text-white py-2 text-xs font-semibold uppercase tracking-widest hover:bg-red-700 transition-colors duration-150">Reject</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
